
/**
 * OLB Login alert scripts.
 *
 * Handles showing active alerts and dismissing them via button clicks.
 */

/** Dependencies -------------------------------------------- */

// External

import jsCookie from 'js-cookie'

// Design system

/** The beef ------------------------------------------------ */

/**
 * Accordion mixin.
 * Provides accordion functionality to single element.
 */
export let mixin = ( { node = null, config = {} } = {} ) => {

    let state = {
        node,
        config,
        breakpoints : [],
        closers     : null
    };

    let module = {

        // Setup

        init() {

            state.breakpoints = [ 'mobile', 'desktop' ]
            state.closers = node.querySelectorAll( state.config.closerSelector )

            this.setEventBindings()
            this.setInitialActiveState()
        },

        setEventBindings() {

            Array.from( state.closers ).map(
				( closer ) => closer.addEventListener( 'click', this.handleCloserClick.bind( this ) )
			)
        },

        // Main

        activateAtBreakpoint( breakpoint ) {
            state.node.classList.add( `${state.config.activeClassPrefix}-${breakpoint}` )
        },

        deactivate() {
            state.breakpoints.forEach(
                ( breakpoint ) => state.node.classList.remove( `${state.config.activeClassPrefix}-${breakpoint}` )
            )
        },

        setInitialActiveState() {

            // First, if the user has their dismissal cookie set, we bail immediately

            if ( this.wasAlertDismissed( state.node.dataset[ state.config.idData ] ) ) return

            // Then, we need to get the current and boundary times as UNIX timestamps

            const currentDatetime = Math.floor( Date.now() / 1000 )
            const startDatetime = parseInt( state.node.dataset[state.config.startDatetimeData] )
            const endDatetime = parseInt( state.node.dataset[state.config.endDatetimeData] )

            // If the alert isn't supposed to be active yet, we bail

            if ( currentDatetime < startDatetime ) return
            if ( currentDatetime > endDatetime ) return

            // Now, we need to check if the user is a member

            const userIsMember = localStorage.getItem( state.config.conciergeStatusStorageKey ) === state.config.memberConciergeStatus

            // For each supported breakpoint, we activate the alert if the user is a member, or the alert is to be shown to all

            for ( const breakpoint of state.breakpoints ) {

                const showToAllAtBreakpoint = !! state.node.dataset[ `${state.config.showToAllDataPrefix}${this.ucFirst( breakpoint )}` ]

                if ( userIsMember || showToAllAtBreakpoint ) {
                    this.activateAtBreakpoint( breakpoint )
                }           
            }
        },

        // Handlers

        handleCloserClick( event ) {

            event.preventDefault()

            this.deactivate()
            this.addDismissedAlert( state.node.dataset[ state.config.idData ] )            
        },

        // Utilities

        wasAlertDismissed( id ) {
            return this.getDismissedAlerts().includes( id )
        },

        getDismissedAlerts() {
            const dismissedString = jsCookie.get( state.config.dismissalCookieName ) || ''
            return dismissedString.split( state.config.dismissalCookieIdDelimiter )
        },

        addDismissedAlert( id ) {

            const dismissedAlerts = this.getDismissedAlerts();
            dismissedAlerts.push( id )

            jsCookie.set( state.config.dismissalCookieName, dismissedAlerts.join( state.config.dismissalCookieIdDelimiter ) )
        },

        ucFirst( string ) {
            return string.charAt( 0 ).toUpperCase() + string.slice( 1 )
        }
    };

    return module.init()
};

/**
 * Accordion module.
 * Provides an interface to apply accordion functionality to all elements matching a selector.
 */
export default {

    init( { selector, config = {} } ) {

        let selected = document.querySelectorAll( selector )

        if ( ! selected.length ) return

        const defaultConfig = {
            closerSelector             : '[data-olb-login-alert-closer]',
            activeClassPrefix          : 'is-active-on',
            idData                     : 'olbAlertId',
            startDatetimeData          : 'startDatetime',
            endDatetimeData            : 'endDatetime',
            showToAllDataPrefix        : 'showToAllOn',
            dismissalCookieName        : 'olb_login_alert_dismissed',
            dismissalCookieIdDelimiter : ',',
            conciergeStatusStorageKey  : 'concierge_status',
            memberConciergeStatus      : 'member'
        }

        return Array.from( selected ).map(
            ( node, index ) => mixin( { node, config: { ...defaultConfig, ...config, selector } } )
        )
    }
}
